/**
 * Mapbox Optimization API Integration
 *
 * Uses Mapbox Optimization API (optimized-trips) to calculate globally optimal
 * route ordering based on actual road network distances, traffic, and turn restrictions.
 *
 * Falls back to local nearest-neighbor algorithm on error or when exceeding waypoint limits.
 *
 * API Docs: https://docs.mapbox.com/api/navigation/optimization/
 */

import { env } from '@/config/environment';
import { RouteStop } from '@/types/technicianRoutes';
import { optimizeRouteStopsFromData } from './routeOptimization';

// =============================================================================
// Types
// =============================================================================

interface MapboxWaypoint {
  waypoint_index: number; // Original index in input coordinates
  trips_index: number; // Position in optimized trip
  location: [number, number]; // [lng, lat]
  name: string;
}

interface MapboxTripLeg {
  distance: number; // Leg distance in meters
  duration: number; // Leg duration in seconds
}

interface MapboxTrip {
  distance: number; // Total distance in meters
  duration: number; // Total duration in seconds
  geometry?: { type: 'LineString'; coordinates: [number, number][] }; // geojson (if requested)
  legs?: MapboxTripLeg[]; // Per-leg metrics in optimized order
}

interface MapboxOptimizationResponse {
  code: string; // 'Ok' on success
  waypoints?: MapboxWaypoint[];
  trips?: MapboxTrip[];
  message?: string; // Error message if code !== 'Ok'
}

export interface OptimizationResult {
  stops: RouteStop[];
  totalDistanceMiles: number;
  totalDurationMinutes: number;
  geometry?: { type: 'LineString'; coordinates: [number, number][] };
  algorithm: 'mapbox_optimization' | 'nearest_neighbor';
}

// =============================================================================
// Constants
// =============================================================================

// Mapbox Optimization API limits
const MAX_WAYPOINTS_FREE_TIER = 12; // Including start location
const MAX_STOPS = MAX_WAYPOINTS_FREE_TIER - 1; // 11 stops max (start + 11 = 12)

const MAPBOX_OPTIMIZATION_URL = 'https://api.mapbox.com/optimized-trips/v1/mapbox/driving';

// =============================================================================
// Fallback
// =============================================================================

/**
 * Fallback to local nearest-neighbor algorithm
 */
function fallbackToNearestNeighbor(
  stops: RouteStop[],
  startLocation: { lat: number; lng: number },
  reason: string
): OptimizationResult {
  // eslint-disable-next-line no-console
  console.log(`[MapboxOptimization] Falling back to nearest-neighbor: ${reason}`);

  const optimizedStops = optimizeRouteStopsFromData(stops, startLocation);

  return {
    stops: optimizedStops,
    totalDistanceMiles: 0,
    totalDurationMinutes: 0,
    algorithm: 'nearest_neighbor'
  };
}

// =============================================================================
// Main Function
// =============================================================================

/**
 * Optimize route stop order using Mapbox Optimization API.
 *
 * Returns stops reordered for optimal travel distance based on actual road network.
 * Falls back to nearest-neighbor algorithm on error or when exceeding limits.
 *
 * @param stops - Array of route stops to optimize
 * @param startLocation - Starting location (technician's base)
 * @returns Optimized stops with distance/duration metadata
 */
export async function optimizeRouteWithMapbox(
  stops: RouteStop[],
  startLocation: { lat: number; lng: number }
): Promise<OptimizationResult> {
  // Early returns for edge cases
  if (stops.length === 0) {
    return {
      stops: [],
      totalDistanceMiles: 0,
      totalDurationMinutes: 0,
      algorithm: 'mapbox_optimization'
    };
  }

  if (stops.length === 1) {
    // Single stop - no optimization needed
    const only = stops[0];
    return {
      stops: only ? [{ ...only, stop_order: 1 }] : [],
      totalDistanceMiles: 0,
      totalDurationMinutes: 0,
      algorithm: 'mapbox_optimization'
    };
  }

  const token = env.routing.mapboxAccessToken;
  if (!token) {
    return fallbackToNearestNeighbor(stops, startLocation, 'No Mapbox API token configured');
  }

  // Check waypoint limit (free tier: 12 total including start)
  if (stops.length > MAX_STOPS) {
    return fallbackToNearestNeighbor(
      stops,
      startLocation,
      `Too many stops (${stops.length} > ${MAX_STOPS} max)`
    );
  }

  // Build coordinates string: lng,lat;lng,lat;...
  // First coordinate is the start location, rest are stops
  const coordinates = [
    `${startLocation.lng},${startLocation.lat}`,
    ...stops.map(s => `${s.lng},${s.lat}`)
  ].join(';');

  // API parameters:
  // - source=first: Start location is fixed (not re-ordered)
  // - roundtrip=false: One-way trip optimization (tech doesn't return to start)
  const url =
    `${MAPBOX_OPTIMIZATION_URL}/${coordinates}` +
    '?source=first&roundtrip=false&geometries=geojson&overview=full&annotations=distance,duration' +
    `&access_token=${token}`;

  try {
    // eslint-disable-next-line no-console
    console.log(`[MapboxOptimization] Optimizing ${stops.length} stops...`);

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Mapbox API HTTP error: ${response.status} ${response.statusText}`);
    }

    const data: MapboxOptimizationResponse = await response.json();

    if (data.code !== 'Ok') {
      throw new Error(`Mapbox API error: ${data.code} - ${data.message || 'Unknown error'}`);
    }

    if (!data.waypoints || !data.trips || data.trips.length === 0) {
      throw new Error('Mapbox API returned incomplete response');
    }

    // Extract trip metrics
    const trip = data.trips[0];
    if (!trip) {
      throw new Error('Mapbox API returned no trip');
    }
    const totalDistanceMiles = trip.distance * 0.000621371; // meters to miles
    const totalDurationMinutes = trip.duration / 60; // seconds to minutes

    // Reorder stops based on optimized waypoint order
    // waypoints[0] is the start location, skip it
    // Sort by trips_index to get the optimized order
    const waypointOrder = data.waypoints
      .slice(1) // Skip start location
      .map((wp, originalOutputIndex) => ({
        originalStopIndex: wp.waypoint_index - 1, // -1 because start is index 0
        optimizedPosition: wp.trips_index - 1, // -1 because start is position 0
        outputIndex: originalOutputIndex
      }))
      .sort((a, b) => a.optimizedPosition - b.optimizedPosition);

    // Leg i is the segment into the optimized stop at position i (waypoint 0 is
    // the start), so legs line up with the optimized output order.
    const legs = trip.legs || [];
    const optimizedStops: RouteStop[] = waypointOrder
      .map((wp, idx) => {
        const src = stops[wp.originalStopIndex];
        if (!src) return null;
        const leg = legs[idx];
        return {
          ...src,
          stop_order: idx + 1,
          // miles stay fractional (NUMERIC column); minutes MUST be whole — the
          // upsert RPC casts duration_from_previous_minutes with ::int.
          ...(leg && {
            distance_from_previous_miles: leg.distance * 0.000621371,
            duration_from_previous_minutes: Math.round(leg.duration / 60)
          })
        };
      })
      .filter((s): s is RouteStop => s !== null);

    // eslint-disable-next-line no-console
    console.log(
      `[MapboxOptimization] Success: ${totalDistanceMiles.toFixed(1)} mi, ` +
        `${totalDurationMinutes.toFixed(0)} min`
    );

    return {
      stops: optimizedStops,
      totalDistanceMiles,
      totalDurationMinutes,
      ...(trip.geometry && { geometry: trip.geometry }),
      algorithm: 'mapbox_optimization'
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    // eslint-disable-next-line no-console
    console.error('[MapboxOptimization] API error:', errorMessage);

    return fallbackToNearestNeighbor(stops, startLocation, errorMessage);
  }
}
